import type { IFieldOptions } from '@axo/form/feature/form-input';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { Text } from '@axo/ui-core/components/typography';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../../../context/AxoForm/AxoForm';
import { useBroker } from '../../../../../../../hooks/useBroker';
import { validateSwedishPnr } from '../../../../../../../utils/Sweden/validateSwedishPnr';
import { useEmailValidation } from '../../../../../../../utils/useEmailValidation';
import type {
  CoApplicantDetailsData,
  SwedenFormData,
} from '../../../../FormData.se.types';
import {
  maritalStatusOptions,
  PHONE_REGEX,
  radioOptions,
} from '../../../../config/globals';
import { gridColumns } from '../../../../config/layouts/grid';
import { getEmailRegisterOptions, requiredField } from '../../../shared-config';

export const CoApplicantDetails = () => {
  const { t } = useTranslation();

  const broker = useBroker();

  const { baseUrl, axoFormData } = useAxoFormContext<SwedenFormData>();

  const { watch, setValue } = useFormContext();

  const mainApplicantEmail = axoFormData['Customer/Email'];

  const { EmailPrompt, formError, enableEmailValidation } = useEmailValidation({
    baseUrl,
    email: watch('CoCustomer/Email'),
    setEmail: (email) => setValue('CoCustomer/Email', email),
  });
  const emailRegisterOptions = useMemo(
    () => getEmailRegisterOptions(enableEmailValidation, mainApplicantEmail),
    [mainApplicantEmail, enableEmailValidation]
  );

  const mainApplicantSSN = axoFormData['Person/SocialSecurityNumber'];
  const ssnRegisterOptions: IFieldOptions = useMemo(
    () => ({
      registerOptions: {
        required: 'SocialSecurityNumber.error.required',
        setValueAs: (value) => value.trim(),
        validate: (value) => {
          if (!validateSwedishPnr(value))
            return 'SocialSecurityNumber.error.pattern';

          if (value === mainApplicantSSN)
            return t('SocialSecurityNumber.error.unique');
        },
      },
    }),
    [mainApplicantSSN, t]
  );

  const mainApplicantPhone = axoFormData['Customer/MobilePhoneNumber'];
  const phoneRegisterOptions: IFieldOptions = useMemo(
    () => ({
      registerOptions: {
        required: 'MobilePhoneNumber.error.required',
        setValueAs: (value) => value.trim(),
        validate: {
          pattern: (value) => {
            const formattedValue = value.replaceAll(/\D/g, '');
            if (!PHONE_REGEX.test(formattedValue)) {
              return 'MobilePhoneNumber.error.pattern';
            }

            if (formattedValue === mainApplicantPhone)
              return t('MobilePhoneNumber.error.unique');
          },
        },
      },
    }),
    [mainApplicantPhone, t]
  );

  return (
    <>
      <Text>
        <b>{t('AddCoApplicant.CoApplicantDetails')}</b>
      </Text>

      {/* // TODO: Fix styles */}
      <Grid columns={gridColumns} style={{ alignItems: 'flex-end' }}>
        <RadioFieldUI<CoApplicantDetailsData>
          name="LoanApplication/CoApplicantIsCohabitant"
          label={t('CoApplicantIsCohabitant.label')}
          data-testid="co-applicant-is-cohabitant-radio"
          radioStyle="both"
          options={radioOptions}
          registerOptions={requiredField}
        />

        {/* empty div so above field is half width*/}
        <div />

        <PhoneFieldUI<CoApplicantDetailsData>
          name="CoCustomer/MobilePhoneNumber"
          label={t('MobilePhoneNumber.label', { context: 'coapp' })}
          data-testid="phone-input"
          {...phoneRegisterOptions}
        />

        <EmailFieldUI<CoApplicantDetailsData>
          name="CoCustomer/Email"
          label={t('Email.label', { context: 'coapp' })}
          data-testid="email-input"
          registerOptions={emailRegisterOptions}
          error={formError}
          confirmationPrompt={<EmailPrompt />}
        />

        <DropdownFieldUI<CoApplicantDetailsData>
          name="LoanApplication/CoApplicantMaritalStatus"
          label={t('MaritalStatus.label', { context: 'coapp' })}
          data-testid="marital-status-dropdown"
          options={maritalStatusOptions[broker]}
          registerOptions={requiredField}
        />

        <NumberFieldUI<CoApplicantDetailsData>
          name="CoPerson/SocialSecurityNumber"
          label={t('SocialSecurityNumber.label', { context: 'coapp' })}
          placeholder={t('SocialSecurityNumber.placeholder')}
          data-testid="ssn-input"
          {...ssnRegisterOptions}
        />
      </Grid>
    </>
  );
};
